import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Image, Clock, Edit, Users, Upload, ExternalLink, Newspaper } from 'lucide-react';
import adminApi from '../api/adminApi';

const StatCard = ({ icon: Icon, label, value, color = 'gold' }) => (
  <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm p-6">
    <div className={`w-10 h-10 rounded-sm flex items-center justify-center mb-4 ${color === 'gold' ? 'bg-[rgba(212,175,55,0.1)]' : 'bg-[rgba(255,255,255,0.05)]'}`}>
      <Icon className={`w-5 h-5 ${color === 'gold' ? 'text-[#D4AF37]' : 'text-white/50'}`} />
    </div>
    <p className={`text-2xl font-bold font-display mb-1 ${color === 'gold' ? 'text-[#D4AF37]' : 'text-white'}`}>{value ?? '—'}</p>
    <p className="text-white/50 text-xs uppercase tracking-wider">{label}</p>
  </div>
);

const QuickLink = ({ to, icon: Icon, label, description }) => (
  <Link to={to} className="flex items-start gap-4 p-5 bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm hover:border-[rgba(212,175,55,0.4)] transition-colors group">
    <div className="w-10 h-10 rounded-sm bg-[rgba(212,175,55,0.1)] flex items-center justify-center flex-shrink-0 group-hover:bg-[rgba(212,175,55,0.2)] transition-colors">
      <Icon className="w-5 h-5 text-[#D4AF37]" />
    </div>
    <div>
      <p className="text-white font-medium text-sm mb-0.5">{label}</p>
      <p className="text-white/40 text-xs">{description}</p>
    </div>
  </Link>
);

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get('/admin/dashboard/stats')
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (iso) => {
    if (!iso) return 'Never';
    return new Date(iso).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-white mb-1">Dashboard</h1>
        <p className="text-white/40 text-sm">Welcome back. Here's an overview of your website.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Mail} label="Unread Messages" value={loading ? '…' : stats?.unread_messages} color="gold" />
        <StatCard icon={Newspaper} label="Published Articles" value={loading ? '…' : stats?.published_articles} color="gold" />
        <StatCard icon={Upload} label="Media Files" value={loading ? '…' : stats?.media_count} color="white" />
        <StatCard icon={Clock} label="Last Updated" value={loading ? '…' : (stats?.last_updated ? formatDate(stats.last_updated).split(',')[0] : 'Never')} color="white" />
      </div>

      {stats?.last_updated && (
        <div className="bg-[rgba(212,175,55,0.05)] border border-[rgba(212,175,55,0.2)] rounded-sm px-5 py-3 text-sm text-white/60">
          Last content update: <span className="text-[#D4AF37]">{formatDate(stats.last_updated)}</span>
          {stats.updated_by && <> by <span className="text-white/80">{stats.updated_by}</span></>}
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm uppercase tracking-wider text-white/40 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <QuickLink to="/admin/hero" icon={Image} label="Edit Hero" description="Update slides, images, and text" />
          <QuickLink to="/admin/products" icon={Edit} label="Edit Products" description="Manage product categories" />
          <QuickLink to="/admin/messages" icon={Mail} label="View Messages" description={stats?.unread_messages > 0 ? `${stats.unread_messages} unread messages` : 'Check contact submissions'} />
          <QuickLink to="/admin/about" icon={Users} label="Edit About" description="Update company info and values" />
          <QuickLink to="/admin/media" icon={Upload} label="Media Library" description="Upload and manage images" />
          <QuickLink to="/admin/news" icon={Newspaper} label="News Articles" description={stats?.total_articles > 0 ? `${stats.total_articles} articles total` : 'Create and manage news articles'} />
          <QuickLink to="/admin/settings" icon={ExternalLink} label="Settings" description="Logo, theme, and language defaults" />
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
